import { useEffect, useState } from 'react';
import { getIntegrations } from 'views/integrations/api';
import { Integration } from 'views/integrations/types';
import GenericTable from 'components/table';
import { createColumnHelper } from '@tanstack/react-table';
import { FaCheck } from 'react-icons/fa';
import { useTranslation } from 'hooks/useTranslation';

interface IntegrationStepProps {
  formData: {
    name: string;
    description: string;
    icon: File | null;
    knowledgeBase: any[];
    workflows: string[];
    integration_ids?: string[];
  };
  setFormData: (data: any) => void;
}

const columnHelper = createColumnHelper<Integration>();

const IntegrationStep = ({ formData, setFormData }: IntegrationStepProps) => {
  const { t } = useTranslation();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoading(true);
        const data = await getIntegrations({ skip: 0, take: 100 });
        setIntegrations(data.items);
        setError(null);
      } catch (err) {
        setError(t('agents.newAgent.failedToFetchIntegrations'));
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, []);

  const selectedIds = formData.integration_ids || [];
  const allSelected = integrations.length > 0 && selectedIds.length === integrations.length;

  const Checkbox = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <label className="flex cursor-pointer items-center justify-center">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded border-2 transition-colors ${
          checked
            ? "border-blue-500 bg-blue-500"
            : "border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800"
        }`}
      >
        {checked && <FaCheck size={12} className="text-white" />}
      </span>
    </label>
  );

  const columns = [
    columnHelper.accessor('id', {
      id: 'select',
      size: 32,
      meta: {
        headerClassName: 'w-[32px] !p-1 !text-center',
        cellClassName: 'w-[32px] !p-1 !text-center',
      },
      header: () => (
        <Checkbox
          checked={allSelected}
          onChange={() => {
            const allIds = allSelected ? [] : integrations.map((i) => i.id);
            setFormData({ ...formData, integration_ids: allIds });
          }}
        />
      ),
      cell: ({ row }) => {
        const isSelected = selectedIds.includes(row.original.id);
        return (
          <Checkbox
            checked={isSelected}
            onChange={() => {
              const newIds = isSelected
                ? selectedIds.filter((id) => id !== row.original.id)
                : [...selectedIds, row.original.id];
              setFormData({ ...formData, integration_ids: newIds });
            }}
          />
        );
      },
      enableSorting: false,
    }),
    columnHelper.accessor('name', {
      header: t('common.name'),
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('integration_type', {
      header: t('integrations.typeLabel'),
      cell: (info) => info.getValue(),
    }),
  ];

  if (loading) {
    return <div>{t('agents.newAgent.loading')}</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold mb-4">{t('agents.newAgent.selectIntegrations')}</h2>
      <GenericTable
        columns={columns}
        data={integrations}
        searchPlaceholder={t('agents.newAgent.searchForIntegration')}
      />
    </div>
  );
};

export default IntegrationStep; 